import AppButton from '@nxsys/components/AppButton';
import AppLoader from '@nxsys/components/AppLoader';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppSwitch from '@nxsys/components/AppSwitch';
import Inheritable from '@nxsys/components/Inheritable';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import {
  defaultCurrentCyOption,
  paymentModelOptionsIreland,
} from '@nxsys/constants/AppConstants';
import { IRELAND } from '@nxsys/store/constants/keys';
import { useUpdateAgencyById } from '@nxsys/hooks/api/agency';
import { UpdateAgencyByIdParams } from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';
import type { Option } from '@nxsys/types/models/Common';
import type { FormProps } from 'antd';
import { Col, Form, Row, Space, Switch, Tooltip, Typography } from 'antd';
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';

export type AgencyGeneralInformationFormValues = {
  agencyName?: string;
  agencyPrefix?: string;
  currency?: Option<string> | null;
  paymentModel?: Option<string>[];
  allowPortalAccess?: boolean | null;
  enableBenefits?: boolean | null;
  timesheetApprovalNeeded?: 'TRUE' | 'FALSE' | 'NOT_AVAILABLE' | string;
  timesheetApprover?: Option<string>[];
};

export type GeneralInformationRef = {
  submit: () => void;
};

type Props = {
  data?: IAgency | null;
  mode?: 'edit' | 'create';
  initialValues?: Partial<AgencyGeneralInformationFormValues>;
  onSubmit?: (values: AgencyGeneralInformationFormValues) => void;
  hideActions?: boolean;
  ref?: React.Ref<GeneralInformationRef>;
};

const defaultEmptyGeneralValues: AgencyGeneralInformationFormValues = {
  agencyName: undefined,
  agencyPrefix: undefined,
  currency: null,
  paymentModel: [],
  allowPortalAccess: null,
  enableBenefits: null,
  timesheetApprovalNeeded: undefined,
  timesheetApprover: [],
};

const GeneralInformation = forwardRef<GeneralInformationRef, Props>(
  function GeneralInformation(
    {
      data,
      mode = 'edit',
      initialValues: externalInitialValues,
      onSubmit,
      hideActions = false,
    },
    ref,
  ) {
    const { messages } = useTypedIntl();
    const params = useParams();
    const agencyId = params.agencyId;
    const isCreateMode = mode === 'create' || !data;

    const [resetKey, setResetKey] = useState(0);

    const {
      run: updateAgency,
      loading: isUpdatingAgency,
      value: updateAgencyValue,
    } = useUpdateAgencyById();

    const effectiveData = updateAgencyValue?.data ?? data ?? null;
    const appStore = useSelector((state: AppState) => state.appStore);
    const isIreland = appStore?.payrollCountry === IRELAND;
    const paymentModelOptions = useMemo(() => {
      return isIreland
        ? paymentModelOptionsIreland
        : (appStore?.appConfigs?.paymentModels ?? []);
    }, [appStore?.appConfigs?.paymentModels, isIreland]);

    const [form] = Form.useForm<AgencyGeneralInformationFormValues>();
    const watchTimesheetApproval = Form.useWatch(
      'timesheetApprovalNeeded',
      form,
    );
    const watchValues = Form.useWatch([], form);

    const defaultValues = useMemo(() => {
      if (isCreateMode) {
        return { ...defaultEmptyGeneralValues, ...externalInitialValues };
      }
      return {
        agencyName: effectiveData?.name,
        timesheetApprovalNeeded:
          effectiveData?.timesheetApprovalNeeded === 'TRUE',
        allowPortalAccess: effectiveData?.allowPortalAccess,
        enableBenefits: effectiveData?.enableBenefits,
        paymentModel: effectiveData?.paymentModel
          ? effectiveData.paymentModel.map((item: string) => ({
              label: item,
              value: item,
            }))
          : [],
        currency: effectiveData?.currency
          ? (defaultCurrentCyOption.find(
              (item) => item.value === effectiveData?.currency,
            ) ?? null)
          : null,
        agencyPrefix: effectiveData?.agencyPrefix,
        timesheetApprover: effectiveData?.timesheetApprover
          ? effectiveData.timesheetApprover.map((item: any) => ({
              label: item.name,
              value: item.id,
            }))
          : [],
      };
    }, [effectiveData, isCreateMode, externalInitialValues]);

    useImperativeHandle(ref, () => ({
      submit: () => form.submit(),
    }));

    const handleSubmit = (values: AgencyGeneralInformationFormValues) => {
      if (isCreateMode && onSubmit) {
        onSubmit(values);
        return;
      }
      if (!agencyId || !effectiveData) return;

      const params: UpdateAgencyByIdParams = {
        name: values.agencyName ?? '',
        agencyPrefix: values.agencyPrefix ?? '',
        currency: values.currency?.value ?? null,
        paymentModel: values.paymentModel?.map((item) => item.value) ?? [],
        allowPortalAccess: values.allowPortalAccess ?? null,
        enableBenefits: values.enableBenefits ?? null,
        emergencyContactName: effectiveData.emergencyContactName || '',
        emergencyMobile: effectiveData.emergencyMobile || null,
        emergencyEmail: effectiveData.emergencyEmail || null,
        emergencyTelephone: effectiveData.emergencyTelephone || null,
        convertRate: effectiveData.convertRate || '',
        email: effectiveData.email || null,
        mobile: effectiveData.mobile || null,
        address: (effectiveData.address || []).map((v) => v ?? ''),
        city: effectiveData.city || '',
        county: effectiveData.county || '',
        postCode: effectiveData.postCode || '',
        country: effectiveData.country || '',
        onboardingEmailNotification:
          effectiveData.onboardingEmailNotification || [],
        timesheetApprovalNeeded:
          typeof values.timesheetApprovalNeeded === 'boolean'
            ? values.timesheetApprovalNeeded
              ? 'TRUE'
              : 'FALSE'
            : values.timesheetApprovalNeeded || '',
        contactName: effectiveData.contactName || '',
        timesheetApprover:
          values.timesheetApprover?.map((item) => item.value) || [],
      };

      updateAgency(agencyId, params as UpdateAgencyByIdParams).then(() => {
        showMessage({
          type: 'success',
          content: messages['prospect.agency.updateSuccess'],
        });
        setResetKey((k) => k + 1);
      });
    };

    if (!isCreateMode && !effectiveData)
      return (
        <div className="relative h-[100px]">
          <AppLoader />
        </div>
      );

    return (
      <div className="GeneralInformation mx-auto">
        <div className="mx-auto w-full">
          <div className="mb-3">
            <Typography.Text className="font-medium text-gray-800 text-lg">
              {messages['common.generalDetails']}
            </Typography.Text>
          </div>

          <AppFormContainer
            key={resetKey}
            form={form}
            initialValues={defaultValues}
            onFinish={
              ((values: AgencyGeneralInformationFormValues) =>
                handleSubmit(
                  values,
                )) as FormProps<AgencyGeneralInformationFormValues>['onFinish']
            }
            onFinishFailed={(errorInfo: any) => {
              const fields = errorInfo?.errorFields
                ?.map((f: any) => f.errors?.[0])
                .filter(Boolean);
              if (fields?.length) {
                showMessage({
                  type: 'error',
                  content: fields.join('. '),
                });
              }
            }}
            scrollToFirstError
            layout="vertical"
          >
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={messages['common.agencyName']}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  name="agencyName"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <AppInput placeholder="e.g. Nxsys Agency" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={messages['common.agencyPrefix']}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  name="agencyPrefix"
                >
                  <AppInput placeholder="e.g. NxA" className="w-full" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={messages['common.currency']}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  name="currency"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <AppSelect
                    options={defaultCurrentCyOption}
                    placeholder={messages['form.select']}
                    className="w-full !min-w-0"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <div className="flex gap-x-[8px]">
                      {messages['common.paymentModel']}
                      <Tooltip title="Payment Models selected here still be visible to the agency to create a referral/prospect against.">
                        <span className="cursor-pointer">
                          <Icons.IconWarning width={18} height={18} />
                        </span>
                      </Tooltip>
                    </div>
                  }
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  name="paymentModel"
                >
                  <AppSelect
                    options={paymentModelOptions}
                    placeholder={messages['form.select']}
                    mode="multiple"
                    maxTagCount="number"
                    className="w-full !min-w-0"
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={messages['common.allowPortalAccess']}
                  layout="horizontal"
                  labelCol={{ span: 20 }}
                  wrapperCol={{ span: 4 }}
                >
                  <Form.Item
                    name="allowPortalAccess"
                    valuePropName="checked"
                    noStyle
                  >
                    <AppSwitch />
                  </Form.Item>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="enableBenefits" wrapperCol={{ span: 24 }}>
                  <Inheritable<boolean>
                    label={messages['common.enableBenefits'] as string}
                    name="enableBenefits"
                    value={watchValues?.enableBenefits}
                    inheritedValue={false}
                    form={form}
                  >
                    {({ value, onChange, disabled }) => (
                      <Switch
                        checked={value ?? false}
                        disabled={disabled}
                        onChange={onChange}
                      />
                    )}
                  </Inheritable>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={messages['common.timesheetApprovalNeeded']}
                  layout="horizontal"
                  labelCol={{ span: 22 }}
                >
                  <Form.Item
                    name="timesheetApprovalNeeded"
                    valuePropName="checked"
                    noStyle
                  >
                    <AppSwitch />
                  </Form.Item>
                </Form.Item>
              </Col>

              {(watchTimesheetApproval === 'TRUE' ||
                (watchTimesheetApproval as unknown) === true) && (
                <Col xs={24}>
                  <Form.Item
                    label={messages['common.timesheetApprover']}
                    name="timesheetApprover"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <AppSelectSync
                      type="approver"
                      apiType="searchAgencyUsers"
                      placeholder={messages['form.select']}
                      style={{ width: '100%' }}
                      searchId={agencyId}
                      mode="multiple"
                      onChangeValue={(value: any) => {
                        form.setFieldValue('timesheetApprover', value);
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </AppFormContainer>

          {!hideActions && (
            <div className="mt-4 flex">
              <Space>
                <AppButton
                  variant="outlined"
                  type="primary"
                  name={messages['common.cancel'] || 'Cancel'}
                  onClick={() => setResetKey((k) => k + 1)}
                  disabled={isUpdatingAgency}
                />
                <AppButton
                  type="primary"
                  name={messages['common.save'] || 'Save'}
                  onClick={() => form.submit()}
                  loading={isUpdatingAgency}
                  disabled={isUpdatingAgency}
                />
              </Space>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default GeneralInformation;
